import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { IntegrationService } from '../../../../../services/integration.service';
import { ToastrService } from 'ngx-toastr';
import { FNFResponse } from '../../../../../models/response';
import { Subject, takeUntil } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { RestaurantService } from '../../../../../services/restaurant.service';

@Component({
  selector: 'app-uber-eats-delivery',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatIcon,
    CommonModule,
    NgxSpinnerModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
  ],
  templateUrl: './uber-eats-delivery.component.html',
  styleUrl: './uber-eats-delivery.component.css',
})
export class UberEatsDeliveryComponent {
  integrationService = inject(IntegrationService);
  restaurantService = inject(RestaurantService);
  ngxSpinnerService = inject(NgxSpinnerService);
  toastr = inject(ToastrService);
  readonly dialogRef = inject(MatDialogRef<UberEatsDeliveryComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  modules: any = [
    { name: 'Basic restaurant details', iconCheck: false, iconLoading: true, iconDot: false },
    { name: 'Menu categories & items', iconCheck: false, iconLoading: false, iconDot: true },
    { name: 'Orders & order types', iconCheck: false, iconLoading: false, iconDot: true },
    { name: 'Store locations', iconCheck: false, iconLoading: false, iconDot: true },
    { name: 'Finishing setup', iconCheck: false, iconLoading: false, iconDot: true },
  ];

  uberEatsConnectStatus: string = '';
  connectionLoading: boolean = false;
  confirmConnect: boolean = true;
  continueConnect: boolean = false;
  successConnect: boolean = false;
  errorConnect: boolean = false;
  errorAuthorization: boolean = false;

  private destroy$ = new Subject<void>();

  ngOnInit() {
    if (this.data?.uberEatsAuthDetails?.status) {
      this.uberEatsConnectStatus = this.data.uberEatsAuthDetails.status;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  connectUberEats() {
    //this.continueUberEatsMigration();
    if (this.data?.uberEatsAuthDetails?.authorization_url) {
      window.open(this.data.uberEatsAuthDetails.authorization_url, '_blank');
      this.confirmConnect = false;
      this.connectionLoading = true;
      this.errorConnect = false;
      this.successConnect = false;
      this.errorAuthorization = false;
      this.uberEatsConnectStatus = 'CONNECTING';
      this.ngxSpinnerService.show('uberEatsSpinner');
      this.getUberEatsConnectionStatus();
    }
  }

  getUberEatsConnectionStatus() {
    this.integrationService
      .getUberEatsConnectionStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
            return;
          } else if (!response.data) {
            this.toastr.error('No data found!', 'Error');
            return;
          }
          this.uberEatsConnectStatus = response.data.status;
          if (
            response.data.status === 'AUTHORIZED' ||
            response.data?.data?.status === 'AUTHORIZED'
          ) {
            setTimeout(() => {
              this.continueUberEatsMigration();
            }, 1000);
          } else if (
            response.data.status === 'AUTHORIZATION_FAILED' ||
            response.data?.data?.status === 'AUTHORIZATION_FAILED'
          ) {
            this.displayAuthorizationError();
          } else if (
            response.data.status === 'COMPLETED' ||
            response.data?.data?.status === 'COMPLETED' ||
            response.data.status === 'sync_completed' ||
            response.data?.data?.status === 'sync_completed'
          ) {
            this.modules[4].iconCheck = true;
            this.modules[4].iconLoading = false;
            this.modules[4].iconDot = false;
            this.displayConnectionSuccess();
          } else if (
            response.data.status === 'FAILED' ||
            response.data?.data?.status === 'FAILED' ||
            response.data.status === 'sync_failed' ||
            response.data?.data?.status === 'sync_failed'
          ) {
            this.displayConnectionError();
          } else {
            setTimeout(() => {
              this.getUberEatsConnectionStatus();
            }, 3000);
          }
        },
        error: () => {
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        },
      });
  }

  continueUberEatsMigration() {
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = true;
    this.errorConnect = false;
    this.successConnect = false;
    this.errorAuthorization = false;

    setTimeout(() => {
      this.modules[0].iconCheck = true;
      this.modules[0].iconLoading = false;
      this.modules[1].iconLoading = true;
      this.modules[1].iconDot = false;
    }, 5000);
    setTimeout(() => {
      this.modules[1].iconCheck = true;
      this.modules[1].iconLoading = false;
      this.modules[2].iconLoading = true;
      this.modules[2].iconDot = false;
    }, 10000);
    setTimeout(() => {
      this.modules[2].iconCheck = true;
      this.modules[2].iconLoading = false;
      this.modules[3].iconLoading = true;
      this.modules[3].iconDot = false;
    }, 15000);
    setTimeout(() => {
      this.modules[3].iconCheck = true;
      this.modules[3].iconLoading = false;
      this.modules[4].iconLoading = true;
      this.modules[4].iconDot = false;
    }, 20000);
    this.uberEatsMigration();
  }

  uberEatsMigration() {
    this.integrationService
      .uberEatsMigration({})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.displayConnectionError();
            return;
          } else if (!response.data) {
            this.displayConnectionError();
            return;
          }
          this.getUberEatsConnectionStatus();
        },
        error: () => {
          this.displayConnectionError();
        },
      });
  }

  displayAuthorizationError() {
    this.errorAuthorization = true;
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = false;
    this.errorConnect = false;
    this.successConnect = false;
  }

  displayConnectionSuccess() {
    this.errorAuthorization = false;
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = false;
    this.errorConnect = false;
    this.successConnect = true;
  }

  displayConnectionError() {
    this.errorAuthorization = false;
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = false;
    this.errorConnect = true;
    this.successConnect = false;
  }

  finishSetup() {
    this.restaurantService.clearMenuCache().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          console.log(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          console.log('No data found!', 'Error');
        }
        console.log('Restaurant cache cleared successfully', 'Success');
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
      },
    });
    this.dialogRef.close('finishSetup');
  }
}
